import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Center, Flex, Loader, Text } from '@mantine/core';
import { httpRequest } from '../../../../../config/api';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
// eslint-disable-next-line import/no-unresolved
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

GlobalWorkerOptions.workerSrc = pdfWorker;

const REGION_COLORS = [
  {
    border: '#40c057',
    fill: 'rgba(64, 192, 87, 0.12)',
    buttonBg: '#ebfbee',
    buttonText: '#2b8a3e'
  },
  {
    border: '#228be6',
    fill: 'rgba(34, 139, 230, 0.12)',
    buttonBg: '#e7f5ff',
    buttonText: '#1864ab'
  },
  {
    border: '#f59f00',
    fill: 'rgba(245, 159, 0, 0.14)',
    buttonBg: '#fff9db',
    buttonText: '#e67700'
  },
  {
    border: '#ae3ec9',
    fill: 'rgba(174, 62, 201, 0.12)',
    buttonBg: '#f8f0fc',
    buttonText: '#862e9c'
  },
  {
    border: '#f03e3e',
    fill: 'rgba(240, 62, 62, 0.12)',
    buttonBg: '#fff5f5',
    buttonText: '#c92a2a'
  },
  {
    border: '#0ca678',
    fill: 'rgba(12, 166, 120, 0.12)',
    buttonBg: '#e6fcf5',
    buttonText: '#087f5b'
  }
];

function parsePageNumber(sheetName) {
  if (!sheetName) return 1;

  const match = sheetName.match(/(\d+)/);

  return match ? parseInt(match[1], 10) : 1;
}

function clampSelection(selection) {
  return {
    x0: Math.max(0, Math.min(1, selection.x0)),
    y0: Math.max(0, Math.min(1, selection.y0)),
    x1: Math.max(0, Math.min(1, selection.x1)),
    y1: Math.max(0, Math.min(1, selection.y1))
  };
}

function selectionToStyle(selection, width, height) {
  if (!selection || !width || !height) return null;

  const x0 = Math.min(selection.x0, selection.x1);
  const y0 = Math.min(selection.y0, selection.y1);
  const x1 = Math.max(selection.x0, selection.x1);
  const y1 = Math.max(selection.y0, selection.y1);

  return {
    left: `${x0 * width}px`,
    top: `${y0 * height}px`,
    width: `${Math.max((x1 - x0) * width, 1)}px`,
    height: `${Math.max((y1 - y0) * height, 1)}px`
  };
}

export default function PdfViewer({
  periodId,
  fileId,
  sheetName,
  selections,
  onSaveSelection,
  onDeleteSelection,
  isSavingSelection,
  isDeletingSelection,
  onShowTable
}) {
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const dragStartRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const [draftSelection, setDraftSelection] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const pageNumber = parsePageNumber(sheetName);

  useEffect(() => {
    if (!periodId || !fileId || !pageNumber) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setDraftSelection(null);

    async function loadPdfPage() {
      try {
        const blob = await httpRequest.get(`api/periods/${periodId}/files/${fileId}`, {
          params: {
            decrypted_preview: true
          },
          responseType: 'blob'
        });
        if (cancelled) return;

        const rawBlob = blob instanceof Blob ? blob : new Blob([blob], { type: 'application/pdf' });
        const pdfData = new Uint8Array(await rawBlob.arrayBuffer());
        const pdfDocument = await getDocument({ data: pdfData }).promise;
        const page = await pdfDocument.getPage(pageNumber);

        if (cancelled) return;

        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d');

        if (!canvas || !context) return;

        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        setPageSize({ width: viewport.width, height: viewport.height });

        await page.render({ canvasContext: context, viewport }).promise;
      } catch {
        if (!cancelled) setError('Failed to load PDF preview.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadPdfPage();

    return () => {
      cancelled = true;
    };
  }, [periodId, fileId, pageNumber]);

  const visibleSelection = useMemo(() => {
    if (draftSelection) return clampSelection(draftSelection);
    return null;
  }, [draftSelection]);

  const savedSelectionStyles = useMemo(() => {
    if (!selections?.length) return [];

    return selections
      .filter(selection => selection.page_number === pageNumber)
      .map((selection, index) => ({
        id: selection.id,
        color: REGION_COLORS[index % REGION_COLORS.length],
        index,
        style: selectionToStyle(clampSelection(selection.bbox), pageSize.width, pageSize.height)
      }))
      .filter(selection => !!selection.style);
  }, [selections, pageNumber, pageSize.width, pageSize.height]);

  const selectionStyle = useMemo(() => {
    return selectionToStyle(visibleSelection, pageSize.width, pageSize.height);
  }, [visibleSelection, pageSize]);

  const getPointFromEvent = event => {
    const bounds = overlayRef.current?.getBoundingClientRect();
    if (!bounds) return null;

    const x = Math.max(0, Math.min(bounds.width, event.clientX - bounds.left));
    const y = Math.max(0, Math.min(bounds.height, event.clientY - bounds.top));

    return {
      x: bounds.width ? x / bounds.width : 0,
      y: bounds.height ? y / bounds.height : 0
    };
  };

  const handleMouseDown = event => {
    if (!pageSize.width || !pageSize.height) return;

    const point = getPointFromEvent(event);
    if (!point) return;

    dragStartRef.current = point;
    setDraftSelection({ x0: point.x, y0: point.y, x1: point.x, y1: point.y });
    setIsDragging(true);
  };

  const handleMouseMove = event => {
    if (!isDragging || !dragStartRef.current) return;

    const point = getPointFromEvent(event);
    if (!point) return;

    setDraftSelection({
      x0: dragStartRef.current.x,
      y0: dragStartRef.current.y,
      x1: point.x,
      y1: point.y
    });
  };

  const handleMouseUp = () => {
    dragStartRef.current = null;
    setIsDragging(false);
  };

  const handleSaveSelection = () => {
    const bbox = draftSelection;
    if (!bbox) return;

    onSaveSelection(
      {
        pageNumber,
        bbox: clampSelection(bbox)
      },
      {
        onSuccess: onShowTable
      }
    );
  };

  if (error) {
    return (
      <Center id="grid-table-pdf-error" h="100%">
        <Text id="grid-table-pdf-error-text" c="red.6" fz="sm">
          {error}
        </Text>
      </Center>
    );
  }

  return (
    <Box style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Flex align="center" justify="space-between" mb="12px" gap="12px" wrap="wrap">
        <Text fz="13px" c="dimmed">
          Drag over one or more PDF table regions, then save each region to generate the Table view.
        </Text>

        <Button
          id="grid-table-pdf-save-region"
          size="xs"
          onClick={handleSaveSelection}
          loading={isSavingSelection}
          disabled={!visibleSelection}
        >
          Save Region
        </Button>
      </Flex>

      {!!selections?.length && (
        <Flex gap="8px" mb="12px" wrap="wrap">
          {selections
            .filter(selection => selection.page_number === pageNumber)
            .map((selection, index) => (
              <Button
                id={`grid-table-pdf-delete-region-${selection.id}`}
                key={selection.id}
                variant="subtle"
                size="xs"
                onClick={() => onDeleteSelection(selection.id)}
                disabled={isDeletingSelection}
                loading={isDeletingSelection}
                style={{
                  backgroundColor: REGION_COLORS[index % REGION_COLORS.length].buttonBg,
                  color: REGION_COLORS[index % REGION_COLORS.length].buttonText,
                  border: `1px solid ${REGION_COLORS[index % REGION_COLORS.length].border}`
                }}
              >
                Remove Region {index + 1}
              </Button>
            ))}
        </Flex>
      )}

      <Box
        id="grid-table-pdf-scroll-area"
        data-testid="pdf-canvas-scroll-area"
        style={{ flex: 1, minHeight: 0, overflow: 'auto' }}
      >
        <Box
          id="grid-table-pdf-canvas-shell"
          style={{
            position: 'relative',
            width: pageSize.width ? `${pageSize.width}px` : 'fit-content'
          }}
        >
          {isLoading && (
            <Center
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                background: 'rgba(255, 255, 255, 0.7)'
              }}
            >
              <Loader size="md" />
            </Center>
          )}
          <canvas id="grid-table-pdf-canvas" ref={canvasRef} style={{ display: 'block' }} />
          <Box
            id="grid-table-pdf-selection-overlay"
            ref={overlayRef}
            data-testid="pdf-selection-overlay"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{
              position: 'absolute',
              inset: 0,
              cursor: 'crosshair',
              userSelect: 'none',
              pointerEvents: isLoading ? 'none' : 'auto'
            }}
          >
            {savedSelectionStyles.map(selection => (
              <Box
                id={`grid-table-pdf-saved-region-${selection.id}`}
                key={selection.id}
                style={{
                  position: 'absolute',
                  boxSizing: 'border-box',
                  border: `2px solid ${selection.color.border}`,
                  background: selection.color.fill,
                  ...selection.style
                }}
              />
            ))}
            {selectionStyle && (
              <Box
                id="grid-table-pdf-draft-region"
                style={{
                  position: 'absolute',
                  boxSizing: 'border-box',
                  border: '2px solid #228be6',
                  background: 'rgba(34, 139, 230, 0.16)',
                  ...selectionStyle
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
